import React, { useState, useEffect } from 'react';
import { FaTimes, FaEnvelope, FaImages, FaClock } from 'react-icons/fa';
import PageChrome from '../../components/layout/PageChrome';
import TravelCard from '../../components/cards/TravelCard';
import travelOpportunities from '../../cms/travel.json';
import pageIntros from '../../cms/page-intros.json';
import RichText from '../../components/ui/RichText';
import '../../css/travel/TravelOpportunities.css';

// Helper function to preload an image
const preloadImage = (src) => {
  const img = new Image();
  img.src = src;
};

const intro = pageIntros.travel || {};

const TravelOpportunities = () => {
  const [selectedTravel, setSelectedTravel] = useState(null);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);

  // Preload first image of a travel opportunity
  const preloadFirstImage = (travel) => {
    if (travel?.photos?.enabled && travel.photos.images.length > 0) {
      preloadImage(travel.photos.images[0].src);
    }
  };

  const handleCardClick = (travel) => {
    // Preload first image on click (useful for touch devices)
    preloadFirstImage(travel);
    setSelectedTravel(travel);
    setCurrentPhotoIndex(0);
  };

  // Handle hover for desktop devices
  const handleCardHover = (travel) => {
    // Only preload on hover for non-touch devices
    if (!('ontouchstart' in window)) {
      preloadFirstImage(travel);
    }
  };

  const closeModal = () => {
    setSelectedTravel(null);
    setCurrentPhotoIndex(0);
  };

  const nextPhoto = () => {
    if (selectedTravel?.photos?.enabled && selectedTravel.photos.images.length > 1) {
      setCurrentPhotoIndex((prev) => 
        prev === selectedTravel.photos.images.length - 1 ? 0 : prev + 1
      );
    }
  };

  const prevPhoto = () => {
    if (selectedTravel?.photos?.enabled && selectedTravel.photos.images.length > 1) {
      setCurrentPhotoIndex((prev) => 
        prev === 0 ? selectedTravel.photos.images.length - 1 : prev - 1
      );
    }
  };

  // Preload adjacent images for smooth navigation
  useEffect(() => {
    if (!selectedTravel?.photos?.enabled || selectedTravel.photos.images.length <= 1) {
      return;
    }

    const images = selectedTravel.photos.images;
    const totalImages = images.length;

    // Preload next image
    const nextIndex = currentPhotoIndex === totalImages - 1 ? 0 : currentPhotoIndex + 1;
    preloadImage(images[nextIndex].src);

    // Preload previous image
    const prevIndex = currentPhotoIndex === 0 ? totalImages - 1 : currentPhotoIndex - 1;
    preloadImage(images[prevIndex].src);
  }, [selectedTravel, currentPhotoIndex]);

  const getStatusBadge = (status) => {
    switch (status) {
      case 'open':
        return <span className="travel-modal-status-badge open">Applications Open</span>;
      case 'closed':
        return <span className="travel-modal-status-badge closed">Applications Closed</span>;
      case 'coming-soon':
        return <span className="travel-modal-status-badge coming-soon">Coming Soon</span>;
      default:
        return null;
    }
  };

  return (
    <div className="travel-opportunities-page">
      <PageChrome
        title={intro.title}
        subtitle={intro.subtitle}
        intro={intro.intro}
      >
        <div className="travel-opportunities-container">
          <div className="travel-grid">
            {travelOpportunities.map((travel) => (
              <TravelCard 
                key={travel.id} 
                travel={travel} 
                onClick={() => handleCardClick(travel)}
                onHover={() => handleCardHover(travel)}
              />
            ))}
          </div>
        </div>
      </PageChrome>

      {/* Modal */}
      {selectedTravel && (
        <div className="travel-modal-overlay" onClick={closeModal}>
          <div className="travel-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="travel-modal-close" onClick={closeModal}>
              <FaTimes />
            </button>
            
            <div className="travel-modal-header">
              <h2 className="travel-modal-title">{selectedTravel.name}</h2>
              {getStatusBadge(selectedTravel.applicationStatus)}
              <div className="travel-modal-details">
                <FaClock className="travel-modal-details-icon" /> {selectedTravel.duration}
              </div>
              <div className="travel-modal-contact">
                <FaEnvelope className="travel-modal-contact-icon" />
                <a 
                  href={`mailto:${selectedTravel.contact.email}`}
                  className="travel-modal-contact-link"
                >
                  {selectedTravel.contact.name}
                </a>
              </div>
            </div>

            <div className="travel-modal-body">
              {/* Photo Gallery */}
              {selectedTravel.photos?.enabled && selectedTravel.photos.images.length > 0 && (
                <div className="travel-photo-gallery">
                  <div className="travel-photo-container">
                    <img 
                      src={selectedTravel.photos.images[currentPhotoIndex].src} 
                      alt={selectedTravel.photos.images[currentPhotoIndex].alt}
                      className="travel-photo"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                    <div className="travel-photo-placeholder">
                      <FaImages />
                      <p>Photo coming soon</p>
                    </div>
                    
                    {selectedTravel.photos.images.length > 1 && (
                      <>
                        <button className="travel-photo-nav prev" onClick={prevPhoto}>
                          ‹
                        </button>
                        <button className="travel-photo-nav next" onClick={nextPhoto}>
                          ›
                        </button>
                        <div className="travel-photo-indicators">
                          {selectedTravel.photos.images.map((_, index) => (
                            <span 
                              key={index} 
                              className={`travel-photo-indicator ${index === currentPhotoIndex ? 'active' : ''}`}
                              onClick={() => setCurrentPhotoIndex(index)}
                            />
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                  <p className="travel-photo-caption">
                    {selectedTravel.photos.images[currentPhotoIndex].caption}
                  </p>
                </div>
              )}

              <RichText value={selectedTravel.description} className="travel-modal-description" />

              <div className="travel-modal-details-section">
                <div className="travel-detail-item">
                  <strong>Eligibility:</strong> {selectedTravel.eligibility}
                </div>
                <div className="travel-detail-item">
                  <strong>Cost:</strong> {selectedTravel.cost}
                </div>
              </div>

              {selectedTravel.highlights && selectedTravel.highlights.length > 0 && (
                <div className="travel-highlights">
                  <h4>Highlights:</h4>
                  <ul>
                    {selectedTravel.highlights.map((highlight, index) => (
                      <li key={index}>{highlight}</li>
                    ))}
                  </ul>
                </div>
              )}

              {selectedTravel.applicationLink && selectedTravel.applicationStatus === 'open' ? (
                <div className="travel-modal-apply">
                  <a 
                    href={selectedTravel.applicationLink} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="travel-modal-apply-button"
                  >
                    Apply Now
                  </a>
                </div>
              ) : selectedTravel.applicationStatus === 'closed' ? (
                <div className="travel-modal-status">
                  <p className="travel-modal-status-message">
                    Applications for this semester are now closed. Check back next semester!
                  </p>
                </div>
              ) : (
                <div className="travel-modal-status">
                  <p className="travel-modal-status-message">
                    Applications for this program will open soon. Please check back later!
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TravelOpportunities; 